import React, { useCallback, useEffect, useState } from 'react';
import type { CrudTableColumn } from '../crud/CrudTable';
import CrudTable, { useCrudState, useStandardCrudHandlers } from '../crud/CrudTable';
import { useCouponService } from '../../services/CouponService';
import type { AbortableRequest } from '@dr-pam/common-components/Utils/FetchUtils';
import type { CouponListFragment, CouponSingleFragment } from '../../graphql/graphql';
import { CouponDiscountType } from '../../graphql/graphql';
import { Flex, Menu, ActionIcon, List, Tooltip, Text, Badge, Group, Tabs } from '@mantine/core';
import {
	IconDotsVertical,
	IconPencil,
	IconTrash,
	IconChartBar,
	IconTicket,
	IconArchive,
	IconRestore,
} from '@tabler/icons-react';
import AddCouponModal from './AddCouponModal';
import EditCouponModal from './EditCouponModal';
import StatusBadge from '../content/StatusBadge';
import { modals } from '@mantine/modals';
import ConfirmModal from '../modals/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';

export default function CouponsList() {
	const couponService = useCouponService();
	const navigate = useNavigate();
	const [selectedTab, setSelectedTab] = useState<string>('active');

	// Completed-payment counts for every coupon, fetched once in a single grouped query rather than
	// per coupon. See CouponService.getUseCounts for why the count cannot live on the coupon itself.
	const [usageCounts, setUsageCounts] = useState<Map<string, number>>(new Map());

	useEffect(() => {
		const request = couponService.getUseCounts();
		let cancelled = false;

		request.response
			.then((counts) => {
				if (!cancelled) {
					setUsageCounts(counts);
				}
			})
			.catch(() => {
				// A failed count must not blank the coupons list; the badges fall back to "Not used".
			});

		return () => {
			cancelled = true;
		};
	}, [couponService]);

	// Separate state for active and archived to enable caching
	const fetchActiveCoupons = useCallback((): AbortableRequest<CouponSingleFragment[]> | null => {
		return couponService.getAll(false);
	}, [couponService]);

	const fetchArchivedCoupons = useCallback((): AbortableRequest<CouponSingleFragment[]> | null => {
		return couponService.getArchived();
	}, [couponService]);

	const activeCrudState = useCrudState<CouponListFragment, CouponSingleFragment>(fetchActiveCoupons);
	const archivedCrudState = useCrudState<CouponListFragment, CouponSingleFragment>(fetchArchivedCoupons);

	// Use the appropriate state based on selected tab
	const crudState = selectedTab === 'archived' ? archivedCrudState : activeCrudState;

	const crudHandlers = useStandardCrudHandlers({
		crudState,
		entityName: 'coupon',
		nameAccessor: (coupon) => coupon.code,
		addModalFactory: (props) => <AddCouponModal {...props} />,
		editModalFactory: (props) => <EditCouponModal {...props} />,
		delete: async (coupon) => {
			const request = couponService.getCouponUses(coupon.id);
			const couponUses = await request.response;

			if (couponUses > 0) {
				// Archive the coupon if it has been used in payments
				const updatedCoupon = await couponService.update(coupon.id, {
					isArchived: { set: true },
				});
				// Optimistically update both states
				archivedCrudState.add(updatedCoupon);
				NotificationUtils.showSuccess(
					'Coupon archived',
					`Coupon "${coupon.code}" has been archived because it has been used in ${couponUses} payment(s).`,
				);
			} else {
				// If no related payments, delete the coupon
				await couponService.delete(coupon.id);
				// The standard handler will call crudState.delete after this
			}
		},
	});

	const handleUnarchive = useCallback(
		async (coupon: CouponListFragment) => {
			const modalId = 'unarchive-coupon';
			modals.open({
				modalId,
				title: 'Unarchive coupon',
				children: (
					<ConfirmModal
						modalId={modalId}
						confirmText="Unarchive"
						onConfirm={async () => {
							try {
								const updatedCoupon = await couponService.update(coupon.id, {
									isArchived: { set: false },
								});
								// Optimistically update both states
								archivedCrudState.delete(coupon);
								activeCrudState.add(updatedCoupon);
								NotificationUtils.showSuccess(
									'Coupon unarchived',
									`Coupon "${coupon.code}" has been restored.`,
								);
							} catch (err) {
								NotificationUtils.showError(err as Error, 'Failed to unarchive coupon');
							}
						}}
					>
						Are you sure you want to unarchive the coupon {coupon.code}?
					</ConfirmModal>
				),
			});
		},
		[couponService, activeCrudState, archivedCrudState],
	);

	const handleViewUsage = useCallback(
		(coupon: CouponListFragment) => {
			navigate(`/reports?tab=coupon-report&couponId=${coupon.id}`);
		},
		[navigate],
	);

	const handleTogglePublished = useCallback(
		(coupon: CouponListFragment) => {
			const modalId = 'publish-coupon';
			if (coupon.isPublished) {
				modals.open({
					modalId,
					title: 'Un-publish coupon',
					children: (
						<ConfirmModal
							modalId={modalId}
							confirmText="Un-publish"
							onConfirm={async () => {
								const updatedCoupon = await couponService.unpublish(coupon.id);
								crudState.setItems(
									(crudState.items ?? []).map((item) =>
										item.id === updatedCoupon.id ? updatedCoupon : item,
									),
								);
							}}
							danger
						>
							Are you sure you want to un-publish the coupon {coupon.code}?
						</ConfirmModal>
					),
				});
			} else {
				modals.open({
					modalId,
					title: 'Publish coupon',
					children: (
						<ConfirmModal
							modalId={modalId}
							confirmText="Publish"
							onConfirm={async () => {
								const updatedCoupon = await couponService.publish(coupon.id);
								crudState.setItems(
									(crudState.items ?? []).map((item) =>
										item.id === updatedCoupon.id ? updatedCoupon : item,
									),
								);
							}}
						>
							Are you sure you want to publish the coupon {coupon.code}?
						</ConfirmModal>
					),
				});
			}
		},
		[couponService, crudState],
	);

	const crudColumns = (coupon: CouponListFragment): CrudTableColumn[] => {
		const uses = usageCounts.get(coupon.id) ?? 0;

		return [
			{
				children: coupon.code,
			},
			{
				children: coupon.description,
			},
			{
				children:
					coupon.discountType === CouponDiscountType.Percentage
						? `${(coupon.percentage ?? 0) * 100}%`
						: `$${(coupon.fixedAmountInCents ?? 0) / 100}`,
			},
			{
				children: (
					<List fz="xs">
						{coupon.restrictedToProducts.map((x) => (
							<List.Item key={x.id}>
								<Tooltip label={x.product.name}>
									<Text fz="xs">{x.product.shortName}</Text>
								</Tooltip>
							</List.Item>
						))}
					</List>
				),
			},
			{
				children: (
					<StatusBadge
						status={coupon.isArchived ? 'archived' : coupon.isPublished ? 'published' : 'draft'}
						onClick={coupon.isArchived ? undefined : () => handleTogglePublished(coupon)}
					/>
				),
			},
			{
				children: (
					<Group gap="xs" justify="center">
						{uses > 0 ? (
							<Tooltip
								label={`Successfully used ${uses} time${
									uses !== 1 ? 's' : ''
								} (completed payments only)`}
							>
								<Badge
									color="blue"
									variant="light"
									size="md"
									style={{ cursor: 'pointer' }}
									onClick={() => handleViewUsage(coupon)}
								>
									{uses} {uses === 1 ? 'use' : 'uses'}
								</Badge>
							</Tooltip>
						) : (
							<Badge color="gray" variant="light" size="sm">
								Not used
							</Badge>
						)}
					</Group>
				),
			},
			{
				children: (
					<Flex justify="flex-end" gap={0} wrap="nowrap" align="center">
						<Menu shadow="md" width={200}>
							<Menu.Target>
								<ActionIcon color="dark" variant="subtle">
									<IconDotsVertical />
								</ActionIcon>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item leftSection={<IconChartBar />} onClick={() => handleViewUsage(coupon)}>
									View Usage
								</Menu.Item>
								{!coupon.isArchived && (
									<Menu.Item
										leftSection={<IconPencil />}
										onClick={() => crudHandlers.editHandler(coupon)}
									>
										Edit
									</Menu.Item>
								)}
								{selectedTab === 'active' && (
									<Menu.Item
										leftSection={<IconTrash />}
										onClick={() => crudHandlers.deleteHandler(coupon)}
										color="red"
									>
										Delete
									</Menu.Item>
								)}
								{selectedTab === 'archived' && (
									<Menu.Item
										leftSection={<IconRestore />}
										onClick={() => handleUnarchive(coupon)}
										color="blue"
									>
										Unarchive
									</Menu.Item>
								)}
							</Menu.Dropdown>
						</Menu>
					</Flex>
				),
			},
		];
	};

	const handleTabChanged = useCallback((tab: string | null) => {
		if (tab) {
			setSelectedTab(tab);
		}
	}, []);

	// Calculate badge counts for tabs
	const activeCount = activeCrudState.items?.length ?? 0;
	const archivedCount = archivedCrudState.items?.length ?? 0;

	return (
		<Tabs value={selectedTab} onChange={handleTabChanged}>
			<Tabs.List mb="md">
				<Tabs.Tab value="active" leftSection={<IconTicket />}>
					Active Coupons{' '}
					{activeCount > 0 && (
						<Badge size="sm" ml="xs">
							{activeCount}
						</Badge>
					)}
				</Tabs.Tab>
				<Tabs.Tab value="archived" leftSection={<IconArchive />}>
					Archived Coupons{' '}
					{archivedCount > 0 && (
						<Badge size="sm" ml="xs">
							{archivedCount}
						</Badge>
					)}
				</Tabs.Tab>
			</Tabs.List>
			<CrudTable
				items={crudState.items}
				isLoading={crudState.isFetching}
				headers={crudHeaders}
				columns={crudColumns}
				entityName="coupon"
				onAddClicked={selectedTab === 'active' ? crudHandlers.addHandler : undefined}
			/>
		</Tabs>
	);
}

const crudHeaders: CrudTableColumn[] = [
	{
		children: 'Code',
	},
	{
		children: 'Description',
	},
	{
		children: 'Discount',
	},
	{
		children: 'Products',
	},
	{
		children: 'Published',
	},
	{
		children: 'Usage',
	},
	{
		children: '',
	},
];
